import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { ParallaxBanner, ParallaxImage, Reveal, SectionHeading } from './Motion';
import { Img } from './Img';
import { LinkedMedia } from './LinkedMedia';

const Cta = ({ label, url, dark }) => {
  if (!label || !url) return null;
  const cls = `inline-flex items-center gap-2 eyebrow link-underline mt-8 ${dark ? 'text-white' : 'text-foreground'}`;
  if (url.startsWith('http')) {
    return <a href={url} target="_blank" rel="noopener noreferrer" className={cls}>{label} <ArrowRight className="h-3.5 w-3.5" /></a>;
  }
  return <Link to={url} className={cls}>{label} <ArrowRight className="h-3.5 w-3.5" /></Link>;
};

export const IconByName = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Compass;
  return <Cmp className={className} />;
};

/** Renders a list of admin-managed content blocks. */
export const BlockRenderer = ({ blocks = [] }) => (
  <>
    {blocks.map((b, i) => {
      switch (b.type) {
        case 'text':
          return (
            <section key={b.id} className="section-y" data-testid={`block-text-${i}`}>
              <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-4"><Reveal><p className="eyebrow text-[hsl(var(--brand-teal))]">{b.label}</p></Reveal></div>
                <div className="md:col-span-7">
                  <Reveal delay={0.05}><h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] tracking-tight">{b.title}</h2></Reveal>
                  <Reveal delay={0.1}><p className="mt-7 text-[15px] sm:text-[17px] leading-[1.9] text-foreground/75 max-w-prose whitespace-pre-line">{b.body}</p></Reveal>
                  <Reveal delay={0.15}><Cta label={b.cta_label} url={b.cta_url} /></Reveal>
                </div>
              </div>
            </section>
          );
        case 'split': {
          const right = b.align === 'right';
          return (
            <section key={b.id} className={`section-y ${i > 0 ? 'pt-0' : ''}`} data-testid={`block-split-${i}`}>
              <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-14 items-center">
                <div className={`md:col-span-7 ${right ? 'md:order-2' : ''}`}>
                  {b.media_source
                    ? <LinkedMedia source={b.media_source} fallback={b.image} alt={b.image_alt || b.title} ratio="aspect-[4/3] md:aspect-[5/4]" testId={`block-split-${i}-media`} />
                    : <ParallaxImage src={b.image} alt={b.image_alt || b.title} ratio="aspect-[4/3] md:aspect-[5/4]" />}
                </div>
                <div className={`md:col-span-5 ${right ? 'md:order-1' : ''}`}>
                  <SectionHeading label={b.label} title={b.title} body={b.body} />
                  <Reveal delay={0.15}><Cta label={b.cta_label} url={b.cta_url} /></Reveal>
                </div>
              </div>
            </section>
          );
        }
        case 'banner':
          return (
            <ParallaxBanner key={b.id} src={b.image} className="my-6" data-testid={`block-banner-${i}`}>
              <div className="max-w-2xl py-20">
                {b.label && <Reveal><p className="eyebrow text-white/70">{b.label}</p></Reveal>}
                <Reveal delay={0.05}><h2 className="font-display text-3xl sm:text-5xl leading-[1.1] mt-4">{b.title}</h2></Reveal>
                {b.body && <Reveal delay={0.1}><p className="mt-5 text-white/85 leading-relaxed text-[15px] sm:text-lg">{b.body}</p></Reveal>}
                <Reveal delay={0.15}><Cta label={b.cta_label} url={b.cta_url} dark /></Reveal>
              </div>
            </ParallaxBanner>
          );
        case 'quote':
          return (
            <section key={b.id} className="section-y linen-wash" data-testid={`block-quote-${i}`}>
              <div className="container-x text-center max-w-3xl">
                <Reveal><p className="font-display text-2xl sm:text-4xl leading-[1.3] text-foreground/90">&ldquo;{b.body || b.title}&rdquo;</p></Reveal>
                {b.label && <Reveal delay={0.1}><p className="eyebrow text-muted-foreground mt-8">{b.label}</p></Reveal>}
              </div>
            </section>
          );
        case 'image':
          return (
            <section key={b.id} className="py-6" data-testid={`block-image-${i}`}>
              <div className="container-x">
                {b.media_source
                  ? <LinkedMedia source={b.media_source} fallback={b.image} alt={b.image_alt || b.title} ratio="aspect-[16/9]" testId={`block-image-${i}-media`} />
                  : <ParallaxImage src={b.image} alt={b.image_alt || b.title} ratio="aspect-[16/9]" />}
                {(b.title || b.body) && <p className="mt-4 text-xs tracking-[0.14em] uppercase text-muted-foreground">{b.title}{b.body ? ` — ${b.body}` : ''}</p>}
              </div>
            </section>
          );
        case 'grid':
          return (
            <section key={b.id} className="section-y" data-testid={`block-grid-${i}`}>
              <div className="container-x">
                <SectionHeading label={b.label} title={b.title} body={b.body} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-12">
                  {(b.items || []).map((it, j) => (
                    <Reveal key={j} delay={j * 0.06} className="rounded-xl border border-border bg-card p-7 shadow-[var(--shadow-soft)]">
                      {it.image ? <Img src={it.image} alt={it.image_alt || it.title || ''} widths={[480, 800]} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" width={800} height={320} className="h-40 w-full object-cover rounded-lg mb-5" /> : <IconByName name={it.icon} className="h-6 w-6 text-[hsl(var(--brand-teal))] mb-5" />}
                      <h3 className="font-display text-xl">{it.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-foreground/70">{it.body}</p>
                    </Reveal>
                  ))}
                </div>
              </div>
            </section>
          );
        default:
          return null;
      }
    })}
  </>
);
